<template>
  <HomeTabsSkeletonLoader
    v-if="loading"
    :is-admin-on="isAdminOn"
  />
  <section
    v-else
    class="mt-7"
    aria-label="Descripted actions"
  >
    <v-tabs
      v-model="tab"
      class="home-tabs"
      hide-slider
    >
      <v-tab
        v-for="{ label, svg } in tabs"
        :key="label"
        :class="svg"
        class="mr-2"
        :ripple="false"
        @mouseenter="homeHover(`${label} Button`)"
        @click="homeClick(`${label} Button`)"
      >
        <DpplSvg
          :file-name="svg"
          class="mr-2"
        />
        {{ label }}
      </v-tab>
    </v-tabs>
    <v-window
      v-model="tab"
      class="d-flex flex-row align-center justify-space-between tabs-items-container"
      continuous
    >
      <Transition
        v-for="{
          options, label, valueProp, primaryColor, secondaryColor,
        } in tabs"
        :key="label"
        name="tab-fade"
      >
        <v-window-item class="ga-3">
          <HomeTabValueProposition
            :value="valueProp"
            :color="primaryColor"
          />
          <NoEditorWrapper
            v-for="({
              cardLabel, description, route, comingSoon, noEditorWrap, isNew,
            }) in options"
            :key="cardLabel"
            :no-editor="noEditorWrap || false"
          >
            <HomeTabCard
              :parent-label="label"
              :card-label="cardLabel"
              :description="description"
              :primary-color="primaryColor"
              :secondary-color="secondaryColor"
              :route="route"
              :coming-soon="comingSoon"
              :is-new="isNew"
            />
          </NoEditorWrapper>
        </v-window-item>
      </Transition>
    </v-window>
  </section>
</template>

<script>
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import HomeTabCard from '@/components/home/HomeTabs/HomeTabCard.vue';
import HomeTabsSkeletonLoader from '@/components/home/HomeTabs/HomeTabsSkeletonLoader.vue';
import HomeTabValueProposition from '@/components/home/HomeTabs/HomeTabValueProposition.vue';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';
import { mapActions } from 'pinia';
import { useTracking } from '@/stores/tracking/tracking';

export default {
  name: 'HomeTabs',
  components: {
    NoEditorWrapper,
    DpplSvg,
    HomeTabCard,
    HomeTabsSkeletonLoader,
    HomeTabValueProposition,
  },
  props: {
    isAdminOn: {
      type: Boolean,
      default: false,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tab: 0,
    };
  },
  computed: {
    selectedTab() { return this.tabs[this.tab]; },
    keyPrefix() { return this.isAdminOn ? 'admin-' : ''; },
  },
  methods: {
    ...mapActions(useTracking, ['homeHover', 'homeClick']),
  },
};
</script>

<style lang="less" scoped>
.v-theme--light.v-tabs > :deep(.v-tabs-bar) {
  background: transparent;
}

.home-tabs {
  background: transparent;

  .v-tab {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0px;
    background: fade(@grey-400, 10%);
    color: rgb(var(--v-theme-textDarkest)) !important;
    text-transform: none;
    width: 186px;
    transition: all 500ms ease;

    :deep(svg) path {
      stroke: @grey-900;
      stroke-width: 1.5px;
      stroke-linecap: round;
      transition: stroke 500ms ease;
    }

    &:before {
      background: transparent;
    }
  }
}

.standardize-icon {
  &:hover, &.v-tab-item--selected {
    color: @basic-pink !important;

    :deep(svg) path {
      stroke: @basic-pink;
    }
  }

  &.v-tab-item--selected {
    background: fade(@basic-pink, 10);
  }
}

.write-icon {
  &:hover, &.v-tab-item--selected {
    color: @basic-green !important;

    :deep(svg) path {
      stroke: @basic-green;
    }
  }

  &.v-tab-item--selected {
    background: fade(@basic-green, 10);
  }
}

.track-icon {
  :deep(svg) path {
    stroke-linejoin: round;
  }

  &:hover, &.v-tab-item--selected {
    color: @basic-orange !important;

    :deep(svg) path {
      stroke: @basic-orange;
    }
  }

  &.v-tab-item--selected {
    background: fade(@basic-orange, 10);
  }
}

.analyze-icon {
  :deep(svg) circle {
    transition: stroke 500ms ease;
  }

  &:hover, &.v-tab-item--selected {
    color: @basic-blue !important;

    :deep(svg) path, :deep(svg) circle {
      stroke: @basic-blue;
    }
  }

  &.v-tab-item--selected {
    background: fade(@basic-blue, 10);
  }
}

.tabs-items-container {
  background: transparent;
  max-width: 768px;
  margin-top: 2px;

  :deep(.v-window__container) {
    width: 768px;
    min-height: 278px;
  }

  .v-window-item {
    display: flex;
    align-content: start;
    flex-wrap: wrap;
    justify-content: flex-start;
    width: 100%;
    min-height: 322px;
  }
}

.tab-fade {
  &-enter {
    opacity: 0;
    transition: all 500ms ease;
  }

  &-enter-to {
    opacity: 1;
    transition: all 500ms ease;
  }

  &-leave, &-leave-active {
    position: absolute;
    top: 0;
  }

  &-leave-to {
    position: absolute;
    opacity: 0;
    transition: all 500ms ease;
  }
}
</style>
